import { zodResolver } from '@hookform/resolvers/zod'
import { useAppSelector, useAppDispatch } from '@redux'
import {
  getEvolveDownloadAction,
  createEvolveDownloadAction,
} from 'src/redux/actions/evolve-landing-management'
import {
  selectEvolveDownloadLoading,
  selectEvolveDownloadCreateLoading,
} from 'src/redux/Slices/evolveLanding-management'
import { EEvolveLandingPageTabs } from '@configs'
import { message } from 'antd'
import { t } from 'i18next'
import queryString from 'query-string'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { IMediaItem } from 'src/interfaces/gallery'
import { downloadSchema, IDownload } from './downloadSchema'

interface IDownloadProps {
  activeTab?: string
}

export const useDownload = ({ activeTab }: IDownloadProps) => {
  const dispatch = useAppDispatch()
  const [isModalLoading, setIsModalLoading] = useState(false)
  const [isChange, setIsChange] = useState(false)

  const [showModalSelectMedia, setShowModalSelectMedia] = useState(false)
  const [selectedField, setSelectedField] = useState<
    'desktop' | 'mobile' | null
  >(null)
  const onChangeDesktopRef = useRef<any>(null)
  const onChangeMobileRef = useRef<any>(null)

  // Redux selectors
  const downloadLlcEvolve = useAppSelector(
    (state) => state.evolveLanding.download
  )
  const getDownloadActionLoading = useAppSelector(selectEvolveDownloadLoading)
  const createDownloadActionLoading = useAppSelector(
    selectEvolveDownloadCreateLoading
  )

  const [imageSrc, setImageSrc] = useState('')
  const [imageMobileSrc, setImageMobileSrc] = useState('')

  const isDisabledSaveButton = useMemo(() => {
    return createDownloadActionLoading || false
  }, [createDownloadActionLoading])

  const { handleSubmit, control, setValue, getValues } = useForm<IDownload>({
    defaultValues: {
      title: '',
      titleInDutch: '',
      content: '',
      contentInDutch: '',
      mediaId: 0,
      mediaMobileId: 0,
      type: 'EVOLVE_DOWNLOAD',
    },
    resolver: zodResolver(downloadSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const onSubmit = async (data: IDownload) => {
    try {
      const response = await dispatch(createEvolveDownloadAction(data)).unwrap()

      if (response.success) {
        message.success({
          content: response?.message ?? t('common:save_success'),
        })
      }
    } catch (error: any) {
      message.error({
        content: error?.message ?? t('error:failed_to_save_landing'),
      })
    }
  }

  const onSave = () => {
    handleSubmit(onSubmit)()
    setIsChange(false)
  }

  const onPreview = (data: IDownload) => {
    const payload = {
      title: data.title,
      content: data.content,
      url: imageSrc,
      urlMobile: imageMobileSrc,
      type: 'download',
    }
    const stringified = queryString.stringify(payload)

    const URL = `${process.env.REACT_APP_PUBLIC_WEB_DOMAIN}/preview-content?${stringified}`
    window.open(URL, '_blank')
  }

  const onCloseModalSelectMedia = () => {
    setShowModalSelectMedia(false)
  }

  const onSelectMedia = (data: IMediaItem) => {
    if (selectedField === 'desktop') {
      setImageSrc(data?.baseUrl ?? '')
      setValue('mediaId', data?.id ?? 0)
      onChangeDesktopRef.current(data?.id ?? 0)
    } else if (selectedField === 'mobile') {
      setImageMobileSrc(data?.baseUrl ?? '')
      setValue('mediaMobileId', data?.id ?? 0)
      onChangeMobileRef.current(data?.id ?? 0)
    }

    setShowModalSelectMedia(false)
    setIsChange(true)
  }

  useEffect(() => {
    if (activeTab === EEvolveLandingPageTabs.DOWNLOAD_LLC) {
      const getDownload = async () => {
        try {
          const responseGetDownload = await dispatch(
            getEvolveDownloadAction()
          ).unwrap()

          if (responseGetDownload.success) {
            setIsChange(false)
          }
        } catch (error: any) {
          // message.error({
          //   content: error.message ?? t('error:failed_to_get_landing'),
          // })
        }
      }
      getDownload()
    }
  }, [activeTab, dispatch, setValue])

  useEffect(() => {
    if (downloadLlcEvolve) {
      if (Object.keys(downloadLlcEvolve).length) {
        setValue('title', downloadLlcEvolve?.title || '')
        setValue('titleInDutch', downloadLlcEvolve?.titleInDutch || '')
        setValue('content', downloadLlcEvolve?.content || '')
        setValue('contentInDutch', downloadLlcEvolve?.contentInDutch || '')
        setValue('mediaId', downloadLlcEvolve?.mediaId || 0)
        setValue('mediaMobileId', downloadLlcEvolve?.mediaMobileId || 0)
        setValue('type', 'EVOLVE_DOWNLOAD')
        setImageSrc(downloadLlcEvolve?.media?.url ?? '')
        setImageMobileSrc(downloadLlcEvolve?.mediaMobile?.url ?? '')
      }
    }
  }, [downloadLlcEvolve])

  return {
    getDownloadActionLoading,
    control,
    setIsChange,
    onChangeDesktopRef,
    setValue,
    setImageSrc,
    setSelectedField,
    setShowModalSelectMedia,
    imageSrc,
    getValues,
    isModalLoading,
    setIsModalLoading,
    onChangeMobileRef,
    isDisabledSaveButton,
    imageMobileSrc,
    onSubmit: handleSubmit(onPreview),
    onSave,
    isChange,
    showModalSelectMedia,
    onCloseModalSelectMedia,
    onSelectMedia,
  }
}
